import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/auth.context";
import { useOrganization } from "@/context/organization.context";
import { useModules } from "@/context/modules.context";
import { useSidebarLinks } from "@/hooks/useSidebarLinks";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Factory,
  Handshake,
  Hotel,
  Landmark,
  Layers,
  LogOut,
  Search,
  Settings,
  ShoppingBag,
  TreePine,
  Truck,
  Users,
  UserCog,
  Utensils,
} from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import authService from "@/services/auth.service";
import { useTranslation } from "react-i18next";
import useDomain from "@/hooks/use-domain";
import { app_env, env_config } from "@/env-config";

const moduleIcons: Record<string, any> = {
  sales: ShoppingBag,
  inventory: Layers,
  finance: Landmark,
  crm: Handshake,
  restaurant: Utensils,
  hotel: Hotel,
  manufacturing: Factory,
};

const moduleColors: Record<string, string> = {
  sales: "#6366f1",
  inventory: "#8b5cf6",
  finance: "#14b8a6",
  crm: "#06b6d4",
  restaurant: "#f97316",
  hotel: "#ec4899",
  manufacturing: "#0ea5e9",
};

export default function ChooseModule() {
  const { t } = useTranslation(["chooseModule", "chooseOrg"]);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const { activeOrg } = useOrganization();
  const { enabledModules } = useModules();
  const { data } = useSidebarLinks();

  const handleSelectModule = (moduleCode: string) => {
    const navItem = data.navMain.find(
      (item: any) => item.module === moduleCode,
    );
    if (navItem?.items?.length > 0) {
      // Find the first item that the user can access (view is true) and is not external
      const firstAccessibleItem = navItem.items.find((item: any) => item?.view);
      if (firstAccessibleItem) {
        navigate(firstAccessibleItem.url);
        return;
      }
    }
    // Fallback to dashboard if no accessible item found
    navigate("/dashboard");
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      logout();
      navigate("/login");
    } catch (error) {
      toast.error(t("errors.logoutFailed"));
    }
  };

  const { orgDomain } = useDomain(env_config[app_env].domain);

  const handleBackToOrgs = () => {
    navigate("/choose-org");
  };

  // Get modules that the user has at least one permission for
  const accessibleModuleCodes = data.navMain
    .filter((nav: any) => nav.module && nav.items?.length > 0)
    .map((nav: any) => nav.module);

  const settingsNavItem = data.navMain.find(
    (nav: any) => nav.alwaysShow && nav.items?.length > 0,
  );

  const organizationSettingsUrl = settingsNavItem?.items?.[0]?.url;
  const hasSettingsAccess = Boolean(organizationSettingsUrl);

  const accessibleModules = enabledModules.filter((module: any) =>
    accessibleModuleCodes.includes(module.moduleCode),
  );

  const filteredModules = accessibleModules.filter((module: any) =>
    module.name?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const getInitials = (name: string) => {
    return name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t("greeting.morning");
    if (hour < 18) return t("greeting.afternoon");
    return t("greeting.evening");
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - User Info & Branding */}
      <div className="hidden lg:flex lg:w-[440px] lg:fixed lg:inset-y-0 lg:left-0 bg-primary dark:bg-primaryDark flex-col p-8 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5 dark:opacity-[2%]">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:4rem_4rem]" />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col h-full">
          {/* Logo */}
          <div className="mb-10">
            <Link to={"/"}>
              <img className="h-7" src="logo_white.png" alt="" />
            </Link>
          </div>

          {/* User Profile Section */}
          <div className="mb-4">
            <p className="text-primary-foreground/60 text-sm font-medium uppercase tracking-wider mb-4">
              {t("loggedInAs")}
            </p>
            <div className="flex items-center gap-4">
              <Avatar className="h-12 w-12 border-[3px] border-white/30 ring-2 ring-white/10">
                <AvatarImage src={user?.photo} />
                <AvatarFallback className="bg-card text-primaryDark text-xl font-semibold">
                  {getInitials(user?.name || user?.email)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-semibold text-base truncate">
                  {user?.name || "User"}
                </h3>
                <p className="text-white/70 text-sm truncate">{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Current Organization */}
          <div className="mb-4 p-3 rounded-md bg-card/5 border border-white/10">
            <p className="text-white/60 text-xs uppercase tracking-wider mb-2">
              {t("currentOrganization")}
            </p>
            <p className="text-white font-medium">{activeOrg?.name}</p>
          </div>

          {/* Divider */}
          <div className="border-t border-white/10 my-2"></div>

          {/* Action Links */}
          <div className="space-y-3 mt-4 flex-1">
            {/* Account Settings - Simple link style */}
            <button
              onClick={() => navigate("/account-settings")}
              className="w-full flex items-center gap-3 px-4 py-2.5 border border-slate-100/20 rounded-md text-white/80 hover:text-white hover:bg-card/5 transition-colors text-left group"
            >
              <UserCog className="h-4 w-4" />
              <span className="text-sm">{t("chooseOrg:accountSettings")}</span>
            </button>

            {!orgDomain && (
              <button
                onClick={handleBackToOrgs}
                className="w-full flex items-center border border-slate-100/20 gap-3 px-4 py-3.5 rounded-md text-white hover:bg-card/10 transition-colors text-left group"
              >
                <div className="h-9 w-9 rounded-md bg-card/10 flex items-center justify-center group-hover:bg-card/20 transition-colors">
                  <ArrowLeft className="h-4 w-4" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="font-medium text-sm leading-4">
                    {t("actions.switchOrganization")}
                  </span>
                  <span className="text-white/60 text-xs">
                    {t("actions.switchOrganizationDesc")}
                  </span>
                </div>
              </button>
            )}

            {user?.isAdmin && !orgDomain && (
              <button
                onClick={() => navigate("/admin")}
                className="w-full border border-slate-100/20 flex items-center gap-3 px-4 py-3.5 rounded-md text-white hover:bg-card/10 transition-colors text-left group"
              >
                <div className="h-9 w-9 rounded-md bg-card/10 flex items-center justify-center group-hover:bg-card/20 transition-colors">
                  <Settings className="h-4 w-4" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="font-medium text-sm leading-4">
                    {t("actions.adminDashboard")}
                  </span>
                  <span className="text-white/60 text-xs">
                    {t("actions.adminDashboardDesc")}
                  </span>
                </div>
              </button>
            )}
            <button
              onClick={handleLogout}
              className="w-full flex items-center border border-slate-100/20 gap-3 px-4 py-3.5 rounded-md text-white hover:bg-card/10 bg-card/5 transition-colors text-left group"
            >
              <div className="h-9 w-9 rounded-md bg-card/10 flex items-center justify-center group-hover:bg-card/20 transition-colors">
                <LogOut className="h-4 w-4" />
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="font-medium text-sm leading-4">
                  {t("actions.signOut")}
                </span>
                <span className="text-white/60 text-xs">
                  {t("actions.signOutDesc")}
                </span>
              </div>
            </button>
          </div>

          {/* Footer */}
          <div className="pt-6 mt-auto">
            <p className="text-white/40 text-sm">{t("footer")}</p>
          </div>
        </div>
      </div>

      {/* Right Panel - Module Selection */}
      <div className="flex-1 flex flex-col bg-backgroud lg:ml-[440px]">
        {/* Mobile Header */}
        <header className="flex items-center justify-between px-4 py-3 bg-card border-b border-border lg:hidden">
          <Link to="/" className="flex items-center gap-2">
            <img className="h-7" src="logo_white.png" alt="" />
          </Link>
          <div className="flex items-center gap-2">
            {/* <SimpleLanguageSwitcher /> */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackToOrgs}
              className="rounded-md"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            {user?.isAdmin && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/admin")}
                className="rounded-md"
              >
                <Settings className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="rounded-md text-muted-foreground"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </header>

        {/* Mobile User Info */}
        <div className="px-4 py-4 bg-card border-b border-border lg:hidden">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={user?.photo} />
              <AvatarFallback className="bg-primary/10 text-primary font-medium">
                {getInitials(user?.name || user?.email)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-muted-foreground text-xs">{getGreeting()},</p>
              <p className="font-medium text-foreground truncate">
                {user?.name || "User"}
              </p>
            </div>
            <Badge variant="outline" className="rounded-md text-xs">
              {activeOrg?.name}
            </Badge>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-3 md:p-6">
          <div className="max-w-xl mx-auto">
            {accessibleModules.length > 0 ? (
              <>
                <div className="mb-6">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
                    <Building2 className="h-3.5 w-3.5" />
                    {activeOrg?.name}
                  </div>
                  <h2 className="text-lg font-semibold text-foreground mb-1">
                    {t("modules.title")}
                  </h2>
                  <p className="text-muted-foreground text-sm">
                    {t("modules.subtitle", { orgName: activeOrg?.name })}
                  </p>
                </div>

                {/* Search */}
                {accessibleModules.length > 4 && (
                  <div className="relative mb-5">
                    <Search className="absolute z-40 left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foregroun" />
                    <Input
                      placeholder={t("modules.search")}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 rounded-md border-border bg-card h-11"
                    />
                  </div>
                )}

                {/* Modules Grid */}
                <div className="grid gap-3">
                  {filteredModules
                    .sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
                    .map((module: any) => {
                      const Icon = moduleIcons[module.moduleCode] || Layers;
                      const color =
                        moduleColors[module.moduleCode] || "#6366f1";
                      const moduleName = t(
                        `modules.names.${module.moduleCode}`,
                        { defaultValue: module.name },
                      );
                      const description = t(
                        `modules.descriptions.${module.moduleCode}`,
                        { defaultValue: t("modules.descriptions.default") },
                      );

                      return (
                        <button
                          key={module.id}
                          onClick={() => handleSelectModule(module.moduleCode)}
                          className="w-full p-3 rounded-md border border-border bg-card hover:border-primary hover:ring-2 hover:ring-primary/10 transition-all text-left group"
                        >
                          <div className="flex items-center gap-4">
                            <div
                              className="flex h-12 w-12 items-center justify-center rounded-md text-white flex-shrink-0"
                              style={{ backgroundColor: color }}
                            >
                              <Icon className="h-5 w-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold text-sm text-foreground truncate">
                                  {moduleName}
                                </h3>
                              </div>
                              <p className="text-sm text-muted-foreground md:truncate">
                                {description}
                              </p>
                            </div>
                            <div className="flex items-center justify-center h-8 w-8 rounded-md bg-accent group-hover:bg-primary group-hover:text-white transition-colors">
                              <ArrowRight className="h-4 w-4" />
                            </div>
                          </div>
                        </button>
                      );
                    })}

                  {filteredModules.length === 0 && searchQuery && (
                    <div className="text-center py-12 bg-card rounded-md border border-border">
                      <Search className="h-8 z-50 w-8 mx-auto mb-3" />
                      <p className="text-base font-medium mb-1 text-foreground">
                        {t("emptySearch.title")}
                      </p>
                      <p className="text-muted-foreground text-sm">
                        {t("emptySearch.description")}
                      </p>
                    </div>
                  )}
                </div>

                {/* Settings Link - only show if user has settings access */}
                {hasSettingsAccess && (
                  <div className="mt-6">
                    <button
                      onClick={() => navigate(organizationSettingsUrl)}
                      className="w-full p-4 rounded-md border border-border bg-card text-left group"
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex h-10 w-10 items-center  text-muted-foreground group-hover:text-white justify-center rounded-md bg-accent group-hover:bg-primary">
                          <Settings className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-sm mb-1 text-foreground group-hover:text-foreground transition-colors">
                            {t("actions.organizationSettings")}
                          </h3>
                          <p className="text-xs text-muted-foreground">
                            {t("actions.organizationSettingsDesc")}
                          </p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-muted-foreground transition-colors" />
                      </div>
                    </button>
                  </div>
                )}
              </>
            ) : (
              /* Empty State */
              <div className="text-center py-16 px-6 bg-card rounded-md border border-border">
                <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-md bg-accent/50">
                  <Layers className="h-8 w-8 text-muted-foregroun" />
                </div>
                <h2 className="text-base font-semibold text-foreground mb-2">
                  {t("emptyState.title")}
                </h2>
                <p className="text-muted-foreground leading-6 text-sm mb-6 max-w-sm mx-auto">
                  {t("emptyState.description")}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  {hasSettingsAccess && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(organizationSettingsUrl)}
                      className="rounded-md"
                    >
                      <Settings className="mr-2 h-4 w-4" />
                      {t("actions.organizationSettings")}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={handleBackToOrgs}
                    className="rounded-md"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    {t("actions.switchOrganization")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Footer */}
        <footer className="px-4 py-3 border-t border-border bg-card lg:hidden">
          <p className="text-center text-muted-foregroun text-xs">
            {t("footer")}
          </p>
        </footer>
      </div>
    </div>
  );
}
